import { useEffect, useId, useMemo } from 'react'
import { useSearchParams } from 'wouter'
import { FileText } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  FormattedDate,
  FormField,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@ideon-api/ideon-ui'
import {
  AUDIENCES,
  formatBytes,
  hasQuarterFolder,
  licensedStates,
  parseFilesParams,
  yearOptions,
  type BulkFilesParams,
} from '../../lib/bulk-files'
import { QUARTERS } from '../../lib/plan-entitlements'
import { useBulkFileArchive, useBulkFileDownload, useBulkFiles, type BulkFilesData } from '../../lib/use-bulk-files'
import { usePermissionSetEntitlements } from '../../lib/use-permission-set-entitlements'
import { Panel } from '../../components/Panel'

interface Option {
  value: string
  label: string
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: readonly Option[]; onChange: (v: string) => void }) {
  const id = useId()
  return (
    <FormField label={label} htmlFor={id} className="w-full sm:w-44">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

const folderLabel = (f: BulkFilesParams) =>
  [f.adapter, AUDIENCES.find((a) => a.value === f.audience)?.label, f.year, f.quarter, f.state].filter(Boolean).join(' · ')

export function FilesTab({ permissionSetKey, adapters }: { permissionSetKey: string; adapters: readonly string[] }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const entitlements = usePermissionSetEntitlements(permissionSetKey)
  // Reads the clock once per mount; the quarter default does not change under the page.
  const now = useMemo(() => new Date(), [])

  const settled = entitlements.status === 'ready' || entitlements.status === 'error'
  const plans = entitlements.status === 'ready' ? entitlements.data.planEntitlements : null
  const folder = parseFilesParams(searchParams, { adapters, plans, now })
  const years = yearOptions(plans, now, folder.year)

  const hasAdapters = adapters.length > 0
  // Wait for entitlements so the default year settles before the first request.
  const files = useBulkFiles(permissionSetKey, hasAdapters && settled ? folder : null)
  const download = useBulkFileDownload(permissionSetKey)
  const archive = useBulkFileArchive(permissionSetKey)

  const folderKey = JSON.stringify(folder)
  useEffect(() => archive.reset(), [folderKey, archive.reset])

  const update = (change: (params: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        change(params)
        return params
      },
      { replace: true }
    )
  // A filter change leaves the open state: it may not exist in the new folder.
  const setFilter = (key: string) => (value: string) =>
    update((params) => {
      params.set(key, value)
      params.delete('state')
    })

  if (!hasAdapters) {
    return <EmptyState icon={<FileText className="h-10 w-10" />} heading="This permission set has no bulk files" />
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-ideon-gray-40 bg-white p-4">
        <FilterSelect label="Adapter" value={folder.adapter} options={adapters.map((a) => ({ value: a, label: a }))} onChange={setFilter('adapter')} />
        <FilterSelect label="Audience" value={folder.audience} options={AUDIENCES} onChange={setFilter('audience')} />
        <FilterSelect label="Year" value={folder.year} options={years.map((y) => ({ value: y, label: y }))} onChange={setFilter('year')} />
        {hasQuarterFolder(folder.audience) && (
          <FilterSelect label="Quarter" value={folder.quarter ?? ''} options={QUARTERS.map((q) => ({ value: q, label: q }))} onChange={setFilter('quarter')} />
        )}
      </div>

      {entitlements.status === 'error' && (
        <p className="text-sm text-ideon-gray-70">Licensing could not load, so licensed states are not marked.</p>
      )}

      <Panel title={folder.state ?? 'Files'} subtitle={folderLabel(folder)}>
        {(files.status === 'idle' || files.status === 'loading') && (
          <div className="px-4 pb-4">
            <Table density="compact">
              <TableSkeleton columns={3} rows={4} />
            </Table>
          </div>
        )}
        {files.status === 'error' && (
          <div className="px-4 pb-4">
            <Alert variant="error" action={<Button onClick={files.retry}>Retry</Button>}>
              Failed to load: {files.message}
            </Alert>
          </div>
        )}
        {files.status === 'ready' && files.data.kind === 'states' && (
          <StateList
            data={files.data}
            licensed={plans ? licensedStates(plans, folder) : null}
            onSelect={(state) => update((params) => params.set('state', state))}
          />
        )}
        {files.status === 'ready' && files.data.kind === 'files' && (
          <FileList
            data={files.data}
            folder={folder}
            download={download}
            archive={archive}
            onBack={folder.state ? () => update((params) => params.delete('state')) : null}
          />
        )}
      </Panel>
    </div>
  )
}

function StateList({ data, licensed, onSelect }: { data: BulkFilesData; licensed: Set<string> | null; onSelect: (state: string) => void }) {
  if (data.states.length === 0) {
    return <EmptyState icon={<FileText className="h-10 w-10" />} heading="No files" description="Nothing is published for this selection." />
  }
  return (
    <div className="overflow-x-auto px-4 pb-4">
      <Table density="compact">
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Period licensed</TableHead>
            <TableHead className="pr-0 text-right">
              <span className="sr-only">Show files</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.states.map((state) => (
            <TableRow key={state}>
              <TableCell className="font-semibold">{state}</TableCell>
              <TableCell>
                {licensed === null ? (
                  '—'
                ) : licensed.has(state) ? (
                  <Badge variant="success" uppercase={false} dot={false}>
                    Licensed
                  </Badge>
                ) : (
                  <Badge variant="neutral" uppercase={false} dot={false}>
                    Not licensed
                  </Badge>
                )}
              </TableCell>
              <TableCell className="pr-0 text-right">
                <Button size="sm" variant="outline" onClick={() => onSelect(state)} aria-label={`Show files for ${state}`}>
                  Show files
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function FileList({
  data,
  folder,
  download,
  archive,
  onBack,
}: {
  data: BulkFilesData
  folder: BulkFilesParams
  download: ReturnType<typeof useBulkFileDownload>
  archive: ReturnType<typeof useBulkFileArchive>
  onBack: (() => void) | null
}) {
  const working = archive.state.status === 'working'
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 px-4 pb-3">
        {onBack && (
          <Button size="sm" variant="text" onClick={onBack}>
            ← All states
          </Button>
        )}
        <Button className="sm:ml-auto" size="sm" variant="outline" disabled={working || data.files.length === 0} onClick={() => void archive.start(folder)}>
          Download all
        </Button>
      </div>
      {archive.state.status === 'working' && (
        <div className="px-4 pb-3">
          <p className="mb-1 text-sm text-ideon-gray-70">Preparing the zip…</p>
          <Progress aria-label="Preparing the zip" value={archive.state.polls} max={archive.state.maxPolls} />
        </div>
      )}
      {archive.state.status === 'failed' && (
        <div className="px-4 pb-3">
          <Alert variant="error">{archive.state.message}</Alert>
        </div>
      )}
      {download.error && (
        <div className="px-4 pb-3">
          <Alert variant="error">{download.error}</Alert>
        </div>
      )}
      {data.files.length === 0 ? (
        <EmptyState icon={<FileText className="h-10 w-10" />} heading="No files" description="Nothing is published for this selection." />
      ) : (
        <div className="overflow-x-auto px-4 pb-4">
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Last modified</TableHead>
                <TableHead className="pr-0 text-right">
                  <span className="sr-only">Download</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.files.map((file) => (
                <TableRow key={file.name}>
                  <TableCell className="font-semibold">{file.name}</TableCell>
                  <TableCell>{formatBytes(file.size)}</TableCell>
                  <TableCell>
                    <FormattedDate value={file.lastModified} showTime />
                  </TableCell>
                  <TableCell className="pr-0 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={download.pendingName !== null}
                      aria-label={`Download ${file.name}`}
                      onClick={() => void download.download(folder, file.name)}
                    >
                      {download.pendingName === file.name ? 'Preparing…' : 'Download'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
